/**
 * Hono SPA handler.
 *
 * Serves SPAs (static files + index.html injection + SPA fallback + Vite dev middleware)
 * on a Hono application. Replaces the Express-based `core/spa-handler.ts` for the Hono layer.
 *
 * Exports:
 *   - `serveSPA(app, spas?, opts?)` — mount all SpaEntry entries onto the Hono app.
 *   - `resetServeSPA()` — close Vite servers and clear registered endpoints (call on stop()).
 */

import type http from 'node:http'
import { resolve, join } from 'node:path'
import { existsSync, readFileSync } from 'node:fs'
import { serveStatic } from '@hono/node-server/serve-static'
import type { HttpBindings } from '@hono/node-server'

import { logger } from '@kernel/logger/index.js'
import { env } from '@kernel/config/env.js'
import { getProjectPath, getLibPath } from '@kernel/config/paths.js'
import { getInstanceInfo } from '@kernel/config/instance-id.js'
import type { SpaEntry } from '@kernel/config/types.js'
import type { HonoApp } from './types.js'

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

const registeredEndpoints = new Set<string>()

/** Vite dev server instances — closed on resetServeSPA(). */
const viteServers: Array<{ close: () => Promise<void> }> = []

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ServeSPAOpts {
  /** Node.js http.Server for Vite HMR WebSocket sharing. Optional. */
  httpServer?: http.Server
}

/**
 * Mount SPA entries on a Hono app.
 *
 * - Entries with only `origin` (CORS-only) are ignored — CORS is handled in `createApp`.
 * - In development with `viteSrc`, mounts Vite middleware with HMR.
 * - In production (or without `viteSrc`), serves static files + index.html fallback with `window.__NEXUS__` injection.
 */
export async function serveSPA(
  app: HonoApp,
  spas?: SpaEntry[],
  opts: ServeSPAOpts = {}
): Promise<void> {
  if (!spas || spas.length === 0) return

  for (const entry of spas) {
    // Skip CORS-only entries (no path or viteSrc to serve from).
    if (!entry.path && !entry.viteSrc) continue

    const endpoint = entry.endpoint ?? '/'

    // Guard: never mount on /api/* paths.
    if (endpoint === '/api' || endpoint.startsWith('/api/')) {
      logger.error(`Cannot mount SPA on ${endpoint} — reserved for API routes`)
      continue
    }

    // Prevent duplicate mounts.
    if (registeredEndpoints.has(endpoint)) {
      logger.warn(`SPA endpoint ${endpoint} already registered — skipping duplicate`)
      continue
    }
    registeredEndpoints.add(endpoint)

    const isDev = env.NODE_ENV !== 'production'

    if (isDev && entry.viteSrc) {
      const srcPath = resolve(getProjectPath(), entry.viteSrc)
      if (!existsSync(srcPath)) {
        logger.warn({ endpoint, viteSrc: entry.viteSrc, resolved: srcPath }, 'Vite source not found — falling back to static')
      } else {
        const mounted = await mountViteDevMiddleware(app, endpoint, srcPath, opts.httpServer)
        if (mounted) continue
      }
    }

    // Production (or Vite not available): static files + index.html fallback.
    if (entry.path) {
      mountStaticSPA(app, endpoint, entry.path)
    }
  }
}

/**
 * Close all Vite dev servers and reset registered endpoints.
 * Call from the server stop() lifecycle.
 */
export async function resetServeSPA(): Promise<void> {
  for (const server of viteServers) {
    try {
      await server.close()
    } catch {
      // Ignore close errors on shutdown.
    }
  }
  viteServers.length = 0
  registeredEndpoints.clear()
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Build the `window.__NEXUS__` injection payload.
 * Mirrors what the Express spa-handler injects.
 */
function buildNexusPayload(extra?: Record<string, unknown>): string {
  const apiUrl = env.BACKEND_URL ? `${env.BACKEND_URL}/api/v1` : '/api/v1'
  const instance = getInstanceInfo()
  const payload: Record<string, unknown> = {
    apiUrl,
    ...(instance ? { instanceId: instance.instanceId } : {}),
    ...extra,
  }
  return JSON.stringify(payload)
}

/**
 * Inject `window.__NEXUS__` script tag before `</head>`.
 */
function injectNexusConfig(html: string, extra?: Record<string, unknown>): string {
  const script = `<script>window.__NEXUS__=${buildNexusPayload(extra)}</script>`
  return html.replace('</head>', `${script}\n</head>`)
}

/**
 * Resolve distPath to an absolute filesystem path.
 * Checks project cwd first, then falls back to lib path.
 */
function resolveDistPath(distPath: string, absolute = false): string | null {
  if (absolute) return distPath
  const projectPath = resolve(getProjectPath(), distPath)
  if (existsSync(projectPath)) return projectPath
  const libPath = resolve(getLibPath(), distPath)
  if (existsSync(libPath)) return libPath
  return null
}

// ---------------------------------------------------------------------------
// Vite dev middleware
// ---------------------------------------------------------------------------

async function mountViteDevMiddleware(
  app: HonoApp,
  endpoint: string,
  srcPath: string,
  httpServer?: http.Server
): Promise<boolean> {
  try {
    const vite = await import('vite')

    const singletonDeps = ['vue', 'vue-router', 'pinia', 'naive-ui']

    // Route Vite's own logs through pino so warnings/errors are not emitted
    // as raw console output (e.g. "WebSocket server error: Port X in use").
    const viteLogger = vite.createLogger('warn', { prefix: '[vite]' })
    viteLogger.info = (msg) => logger.debug({ component: 'vite' }, msg)
    viteLogger.warn = (msg) => logger.warn({ component: 'vite' }, msg)
    viteLogger.error = (msg) => logger.error({ component: 'vite' }, msg)

    const server = await vite.createServer({
      root: srcPath,
      server: {
        middlewareMode: true,
        allowedHosts: true,
        hmr: httpServer ? { server: httpServer } : true,
      },
      resolve: { dedupe: singletonDeps },
      optimizeDeps: { include: singletonDeps },
      // appType 'custom' so Vite does NOT auto-serve index.html — we handle the fallback.
      appType: 'custom',
      clearScreen: false,
      customLogger: viteLogger,
    })

    viteServers.push(server)

    const pattern = endpoint === '/' ? '*' : `${endpoint}/*`

    app.use(pattern, async (c, next) => {
      const bindings = c.env as HttpBindings
      const req = bindings.incoming
      const res = bindings.outgoing

      await new Promise<void>((resolveP) => {
        server.middlewares(req, res, () => resolveP())
      })

      // If Vite handled the response (asset found), we're done.
      if (res.writableEnded) return

      // Otherwise fall through to the SPA index.html fallback below.
      await next()
    })

    // SPA fallback for Vite dev: transform index.html via Vite pipeline.
    const indexPath = join(srcPath, 'index.html')
    const fallbackPattern = endpoint === '/' ? '*' : `${endpoint}/*`

    app.get(fallbackPattern, async (c) => {
      if (c.req.path.startsWith('/api/')) return c.text('Not Found', 404)

      if (!existsSync(indexPath)) {
        return c.text('index.html not found', 404)
      }

      const accept = c.req.header('accept') ?? ''
      // Only serve index.html for HTML requests — skip non-HTML (API calls, etc.)
      if (!accept.includes('text/html') && !accept.includes('*/*')) {
        return c.text('Not Found', 404)
      }

      const rawHtml = readFileSync(indexPath, 'utf-8')
      const transformed = await server.transformIndexHtml(c.req.path, rawHtml)
      const injected = injectNexusConfig(transformed)
      return c.html(injected)
    })

    logger.info({ path: srcPath }, `Vite dev server mounted at ${endpoint} (HMR enabled)`)
    return true
  } catch (err: unknown) {
    const code = (err as NodeJS.ErrnoException).code
    if (code === 'ERR_MODULE_NOT_FOUND' || code === 'MODULE_NOT_FOUND') {
      logger.warn(`vite not installed — falling back to static serving for ${endpoint}`)
      return false
    }
    logger.error({ err }, `Failed to mount Vite dev server at ${endpoint}`)
    return false
  }
}

// ---------------------------------------------------------------------------
// Static SPA (production)
// ---------------------------------------------------------------------------

function mountStaticSPA(app: HonoApp, endpoint: string, distPath: string): void {
  const resolvedPath = resolveDistPath(distPath)

  if (!resolvedPath) {
    logger.warn({ endpoint, distPath, hint: 'Build the frontend first' }, `SPA directory not found`)
    return
  }

  const indexPath = join(resolvedPath, 'index.html')
  if (!existsSync(indexPath)) {
    logger.warn({ endpoint, resolvedPath }, `index.html not found in SPA directory`)
  }

  // `serveStatic` root is relative to cwd. Convert absolute resolvedPath to relative.
  const cwd = process.cwd()
  const relRoot = resolvedPath.startsWith(cwd)
    ? resolvedPath.slice(cwd.length).replace(/^\//, '')
    : resolvedPath

  // Build injected HTML once (cached in closure).
  let injectedHtml = ''
  if (existsSync(indexPath)) {
    const rawHtml = readFileSync(indexPath, 'utf-8')
    injectedHtml = injectNexusConfig(rawHtml)
  }

  /**
   * Detect whether a request path looks like a file asset (has a file extension).
   * Paths without an extension are treated as SPA routes → serve index.html.
   */
  function isAssetPath(path: string): boolean {
    const filename = path.split('/').pop() ?? ''
    return filename.includes('.') && !filename.endsWith('.html')
  }

  const pattern = endpoint === '/' ? '*' : `${endpoint}/*`

  // For HTML requests (non-asset), serve injected index.html immediately.
  // This MUST be registered before serveStatic so it takes priority for `/` and SPA routes.
  app.get(pattern, (c, next) => {
    const path = c.req.path

    // Never intercept API routes — the /api/* 404 handler returns JSON for unmatched endpoints.
    if (path.startsWith('/api/')) return next()

    const accept = c.req.header('accept') ?? ''
    const wantsHtml = accept.includes('text/html') || accept.includes('*/*')

    // Asset request → let serveStatic handle it.
    if (isAssetPath(path)) return next()

    // HTML (or wildcard-accept) non-asset request → serve injected index.html.
    if (wantsHtml) {
      if (!injectedHtml) return c.text('index.html not found', 404)
      c.header('Cache-Control', 'no-cache, no-store, must-revalidate')
      return c.html(injectedHtml)
    }

    // Non-HTML non-asset (e.g. /unknown.json) → 404.
    return c.text('Not Found', 404)
  })

  // Serve static asset files (JS, CSS, images, etc.).
  app.use(
    pattern,
    serveStatic({
      root: relRoot,
      rewriteRequestPath: (path) => {
        // Strip the endpoint prefix so `/admin/assets/app.js` → `/assets/app.js`
        if (endpoint !== '/' && path.startsWith(endpoint)) {
          return path.slice(endpoint.length) || '/'
        }
        return path
      },
    })
  )

  // Final catch-all for assets that weren't found (404 for real missing files).
  app.get(pattern, (c) => {
    return c.text('Not Found', 404)
  })

  logger.info({ path: resolvedPath }, `SPA mounted at ${endpoint}`)
}
